import { ResidentBody, ResidentItem } from '@lmc/ts-common'
import { ResidentDemographicsUpdate } from '@lmc/validations'
import requests from 'api'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { loadList } from 'operations/Data'
import {
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query'
import { useDispatch } from 'react-redux'

/**
 * Extends Partial<ResidentBody> with demographics fields from @lmc/validations.
 * TODO: Simplify once ResidentBody is migrated away from @lmc/ts-common.
 */
export type ResidentUpdateBody = Partial<ResidentBody> & {
  demographics?: ResidentDemographicsUpdate
}

type UseResidentProps = {
  id: string | undefined
  options?: Pick<UseQueryOptions<{ result: ResidentItem }>, 'onSuccess'>
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
}

export type UpdateResidentReturnType =
  | {
      result: ResidentItem
    }
  | unknown

type UseResidentReturn = {
  resident: ResidentItem | undefined
  isLoading: boolean
  isLoadingDelete: boolean
  isLoadingUpdateDnacpr: boolean
  isLoadingUpdate: boolean
  refetch: () => void
  updateResident: (
    values: ResidentUpdateBody
  ) => Promise<UpdateResidentReturnType>
  updateResidentFormData: (
    formData: FormData
  ) => Promise<UpdateResidentReturnType>
  deleteResident: () => Promise<unknown>
}

export const SEND_RESIDENT_URL = 'list/residents'
const RESIDENT_QUERY_KEY = 'resident'

const useResident = ({
  id,
  options,
  onUpdateSuccess,
  onDeleteSuccess,
}: UseResidentProps): UseResidentReturn => {
  const dispatch = useDispatch()

  const { data, isLoading, refetch } = useQuery(
    [RESIDENT_QUERY_KEY, id] as string[],
    ({ queryKey }) => requests.residents.fetch({ params: { id: queryKey[1] } }),
    {
      onSuccess: options?.onSuccess,
      enabled: Boolean(id) && id?.length === 24,
    }
  )

  const handleError = (err: unknown) => {
    const someError = err as any

    // Try to handle some known cases, or use fallback
    const title = typeof err === 'string'
      ? err
      : (typeof someError?.message === 'string' && someError.message) ||
        (typeof someError?.msg === 'string' && someError.msg) ||
        (typeof someError?.error === 'string' && someError.error) ||
        'Something went wrong'

    showToast({
      title,
      type: 'error',
    })
  }

  const updateFunction = async (
    values: ResidentUpdateBody
  ): Promise<UpdateResidentReturnType> =>
    postJSON({
      url: `${SEND_RESIDENT_URL}/${id}`,
      method: 'PUT',
      json: values,
    })

  const updateDnacprFunction = async (
    formData: FormData
  ): Promise<UpdateResidentReturnType> =>
    postJSON({
      url: `${SEND_RESIDENT_URL}/${id}`,
      method: 'PUT',
      contentType: 'multipart/form-data',
      body: formData,
    })

  const deleteFunction = () =>
    postJSON({
      url: `${SEND_RESIDENT_URL}/${id}`,
      method: 'DELETE',
    })

  const { mutateAsync: updateMutation, isLoading: isLoadingUpdate } =
    useMutation<
      UpdateResidentReturnType,
      unknown,
      ResidentUpdateBody,
      unknown
    >([`update-resident-${id}`], updateFunction, {
      onSuccess: () => {
        dispatch(loadList('residents') as any)
        onUpdateSuccess?.()
      },
      onError: (err) => handleError(err),
    })

  const {
    mutateAsync: updateDnacprMutation,
    isLoading: isLoadingUpdateDnacpr,
  } = useMutation<UpdateResidentReturnType, unknown, FormData, unknown>(
    [`update-resident-${id}`],
    updateDnacprFunction,
    {
      onSuccess: () => {
        dispatch(loadList('residents') as any)
        showToast({
          title: 'DNACPR Form Uploaded',
        })
      },
      onError: (err) => handleError(err),
    }
  )

  const { mutateAsync: deleteMutation, isLoading: isLoadingDelete } =
    useMutation([`delete-resident-${id}`], deleteFunction, {
      onSuccess: () => {
        dispatch(loadList('residents') as any)
        onDeleteSuccess?.()
      },
      onError: (err) => handleError(err),
    })

  const resident = data?.result

  const queryClient = useQueryClient()
  const handleUpdate = async (values: ResidentUpdateBody) => {
    try {
      const result = await updateMutation(values)
      queryClient.invalidateQueries(RESIDENT_QUERY_KEY)
      return result
    } catch (err) {
      return err
    }
  }

  const handleDnacprUpdate = async (values: FormData) => {
    try {
      const result = await updateDnacprMutation(values)
      queryClient.invalidateQueries(RESIDENT_QUERY_KEY)
      return result
    } catch (err) {
      return err
    }
  }

  return {
    resident,
    isLoading,
    isLoadingDelete,
    isLoadingUpdateDnacpr,
    isLoadingUpdate,
    refetch,
    updateResident: handleUpdate,
    updateResidentFormData: handleDnacprUpdate,
    deleteResident: deleteMutation,
  }
}

export const convertFormTypeToResidentBody = <FormType extends object>(
  values: FormType,
  converter: (formTypeValues: FormType) => ResidentUpdateBody
): ResidentUpdateBody => {
  const convertedFormType = converter(values)

  return convertedFormType
}

export default useResident
